import { useState } from 'react';

import { v4 } from 'uuid';

import { getSecurity } from 'api/Filestack';

import { ia } from 'lib/helpers/utility';
import { UPLOAD_API_URL, getFilestackClient } from 'lib/clients/filestack/filestackClient';

import { showAlert } from 'components/shared/Alert/Alert';

import useLoading from './useLoading';

// Keyed by fileId, module-scoped so in-flight uploads stay cancellable when the
// hook remounts while `files` lives in external (Recoil) state
const uploadControllers = new Map();
// Raw File objects kept out of `files` entries: consumers spread entries into
// socket payloads, and socket.io would binary-encode the File
const originalFiles = new Map();

export const useFileStack = (options = {}) => {
  const localFilesState = useState([]);
  const [files, setFiles] = options.filesState ?? localFilesState;
  const { loading, startLoading, stopLoading } = useLoading();

  const updateFileState = (fileId, updates) => {
    setFiles((prev) =>
      prev.map((file) => (file.fileId === fileId ? { ...file, ...updates } : file))
    );
  };

  const handleUploadProgress = (fileId, event) => {
    setFiles((prev) =>
      prev.map((file) => {
        if (file.fileId !== fileId) return file;

        const total = event.total || file.size || 0;
        const uploadedSize = Math.min(event.loaded || 0, file.size || event.loaded || 0);
        const progress = total > 0 ? Math.min((uploadedSize / total) * 100, 100) : file.progress;
        const status = uploadedSize >= (file.size || total) ? null : 'uploading';

        return {
          ...file,
          progress,
          uploadedSize,
          ...(status ? { status } : {})
        };
      })
    );
  };

  const handleUploadSuccess = async (uploadedFile, onUpload) => {
    originalFiles.delete(uploadedFile.fileId);
    if (onUpload) await onUpload(uploadedFile);
    setFiles((prev) =>
      prev.map((file) =>
        file.fileId === uploadedFile.fileId
          ? { ...uploadedFile, addedAt: file.addedAt ?? uploadedFile.addedAt }
          : file
      )
    );
  };

  const getHandleFromUploadResponse = (responseData) => {
    if (responseData?.handle) return responseData.handle;

    const responseUrl = responseData?.url;
    if (typeof responseUrl !== 'string' || !responseUrl) return null;

    try {
      const pathname = new URL(responseUrl).pathname;
      return pathname.split('/').filter(Boolean).pop() || null;
    } catch (_error) {
      return responseUrl.split('?')[0].split('/').filter(Boolean).pop() || null;
    }
  };

  const buildUploadedFile = ({ fileId, newFile, responseData }) => {
    const handle = getHandleFromUploadResponse(responseData);

    if (!handle) return null;

    return {
      fileId,
      handle,
      name: newFile.name,
      size: newFile.size,
      ...(newFile.category ? { category: newFile.category } : {}),
      uploadedSize: newFile.size,
      type: newFile.type,
      url: responseData?.url,
      status: 'uploaded',
      progress: 100,
      key: responseData?.key || null
    };
  };

  const addFile = async (newFile, onUpload) => {
    if (!newFile) return;

    const fileId = v4();
    originalFiles.set(fileId, newFile);

    const fileData = {
      fileId,
      category: newFile.category,
      name: newFile.name,
      size: newFile.size,
      uploadedSize: 0,
      type: newFile.type,
      status: 'uploading',
      progress: 0,
      addedAt: Date.now()
    };
    setFiles((prev) => [...prev, fileData]);

    const securityResponse = await getSecurity();
    const { policy, signature } = securityResponse?.security || {};
    const apiKey = process.env.REACT_APP_FILESTACK;

    if (!apiKey || !policy || !signature) {
      showAlert({
        color: 'danger',
        message: "File uploads aren't available right now. Please try again later."
      });
      updateFileState(fileId, { status: 'failed' });
      return null;
    }

    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      const controller = new AbortController();
      uploadControllers.set(fileId, controller);
      xhr.onloadend = () => uploadControllers.delete(fileId);

      const uploadUrl = `${UPLOAD_API_URL}?key=${apiKey}&policy=${policy}&signature=${signature}`;
      xhr.open('POST', uploadUrl, true);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          handleUploadProgress(fileId, event);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          let responseData;
          try {
            responseData = JSON.parse(xhr.responseText);
          } catch (_error) {
            console.error(
              `Filestack returned a non-JSON response for ${newFile.name}:`,
              xhr.responseText
            );
            updateFileState(fileId, { status: 'failed' });
            showAlert({
              color: 'danger',
              message: `Couldn't upload ${newFile.name}. Please try again.`
            });
            resolve(null);
            return;
          }
          const uploadedFile = buildUploadedFile({ fileId, newFile, responseData });

          if (!uploadedFile) {
            updateFileState(fileId, { status: 'failed' });
            showAlert({
              color: 'danger',
              message: `Invalid response for ${newFile.name}. Please try again.`
            });
            resolve(null);
            return;
          }

          handleUploadSuccess(uploadedFile, onUpload);
          resolve(uploadedFile);
        } else {
          updateFileState(fileId, { status: 'failed' });
          showAlert({
            color: 'danger',
            message: `Couldn't upload ${newFile.name}. Please try again.`
          });
          resolve(null);
        }
      };

      xhr.onerror = () => {
        updateFileState(fileId, { status: 'failed' });
        showAlert({
          color: 'danger',
          message: `Couldn't upload ${newFile.name}. Please try again.`
        });
        resolve(null);
      };

      xhr.onabort = () => {
        updateFileState(fileId, { status: 'cancelled' });
        resolve(null);
      };

      controller.signal.onabort = () => xhr.abort();

      const formData = new FormData();
      formData.append('fileUpload', newFile);
      xhr.send(formData);
    });
  };

  const addFiles = async (newFiles, onUpload, onUploadMultiple) => {
    if (!newFiles || newFiles.length === 0) return;

    startLoading();
    try {
      const baseAddedAt = Date.now();
      const fileDataArray = newFiles.map((newFile, idx) => {
        const fileId = v4();
        originalFiles.set(fileId, newFile);
        return {
          fileId,
          name: newFile.name,
          size: newFile.size,
          uploadedSize: 0,
          type: newFile.type,
          ...(newFile.category ? { category: newFile.category } : {}),
          status: 'uploading',
          progress: 0,
          key: newFile.key || null,
          addedAt: baseAddedAt + idx
        };
      });

      setFiles((prev) => [...prev, ...fileDataArray]);

      const securityResponse = await getSecurity();
      const { policy, signature } = securityResponse?.security || {};
      const apiKey = process.env.REACT_APP_FILESTACK;

      if (!apiKey || !policy || !signature) {
        fileDataArray.forEach(({ fileId }) => {
          updateFileState(fileId, { status: 'failed' });
        });
        showAlert({
          color: 'danger',
          message: "File uploads aren't available right now. Please try again later."
        });
        stopLoading();
        return null;
      }

      const uploadUrl = `${UPLOAD_API_URL}?key=${apiKey}&policy=${policy}&signature=${signature}`;

      const results = await Promise.allSettled(
        newFiles.map((newFile, index) => {
          return new Promise((resolve) => {
            const fileId = fileDataArray[index].fileId;
            const xhr = new XMLHttpRequest();
            const controller = new AbortController();
            uploadControllers.set(fileId, controller);
            xhr.onloadend = () => uploadControllers.delete(fileId);

            xhr.open('POST', uploadUrl, true);

            xhr.upload.onprogress = (event) => {
              if (event.lengthComputable) {
                handleUploadProgress(fileId, event);
              }
            };

            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                let responseData;
                try {
                  responseData = JSON.parse(xhr.responseText);
                } catch (_error) {
                  console.error(
                    `Filestack returned a non-JSON response for ${newFile.name}:`,
                    xhr.responseText
                  );
                  updateFileState(fileId, { status: 'failed' });
                  showAlert({
                    color: 'danger',
                    message: `Couldn't upload ${newFile.name}. Please try again.`
                  });
                  resolve({
                    fileId,
                    response: null,
                    newFile,
                    error: new Error('Invalid response')
                  });
                  return;
                }
                resolve({ fileId, response: responseData, newFile });
              } else {
                updateFileState(fileId, { status: 'failed' });
                showAlert({
                  color: 'danger',
                  message: `Couldn't upload ${newFile.name}. Please try again.`
                });
                resolve({ fileId, response: null, newFile, error: new Error('Upload failed') });
              }
            };

            xhr.onerror = () => {
              updateFileState(fileId, { status: 'failed' });
              showAlert({
                color: 'danger',
                message: `Couldn't upload ${newFile.name}. Please try again.`
              });
              resolve({ fileId, response: null, newFile, error: new Error('Upload error') });
            };

            xhr.onabort = () => {
              updateFileState(fileId, { status: 'cancelled' });
              resolve({ fileId, response: null, newFile, error: new Error('Upload aborted') });
            };

            controller.signal.onabort = () => xhr.abort();

            const formData = new FormData();
            formData.append('fileUpload', newFile);
            xhr.send(formData);
          });
        })
      );

      const responses = [];
      const uploadedFiles = [];
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value.response) {
          const { response, newFile, fileId } = result.value;
          const uploadedFile = buildUploadedFile({ fileId, newFile, responseData: response });

          if (!uploadedFile) {
            updateFileState(fileId, { status: 'failed' });
            showAlert({
              color: 'danger',
              message: `Invalid response for ${newFile.name}. Please try again.`
            });
            return;
          }

          if (newFile.category) {
            uploadedFile.category = newFile.category;
          }

          handleUploadSuccess(uploadedFile, onUpload);
          responses.push(response);
          uploadedFiles.push(uploadedFile);
        }
      });

      if (onUploadMultiple) {
        onUploadMultiple(uploadedFiles);
      }

      if (results.some((result) => result.status === 'rejected')) {
        showAlert({
          color: 'danger',
          message: "Couldn't upload some files. You can retry the failed uploads."
        });
      }

      return responses;
    } catch (error) {
      showAlert({ color: 'danger', message: 'An error occurred during upload. Please try again.' });
      return null;
    } finally {
      stopLoading();
    }
  };

  const openFilePicker = async (onUpload, onUploadMultiple) => {
    const fileStackClient = await getFilestackClient();
    if (!fileStackClient) return;

    const defaultOptions = {
      accept: ['image/*', '.pdf', '.png', '.jpeg'],
      maxFiles: 10,
      uploadInBackground: false,
      exposeOriginalFile: true,
      onUploadDone: (data) => {
        if (data && ia(data.filesUploaded)) {
          const baseAddedAt = Date.now();
          const newFiles = data.filesUploaded.map((uploaded, idx) => {
            return {
              fileId: v4(),
              handle: uploaded.handle,
              name: uploaded.filename,
              size: uploaded.size,
              uploadedSize: uploaded.size,
              type: uploaded.mimetype,
              url: uploaded.url,
              status: 'uploaded',
              progress: 100,
              addedAt: baseAddedAt + idx
            };
          });

          setFiles((prev) => [...prev, ...newFiles]);
          newFiles.forEach((fileData) => handleUploadSuccess(fileData, onUpload));

          if (onUploadMultiple) {
            onUploadMultiple(newFiles);
          }
        }
      },
      onFileUploadFailed: () => {
        showAlert({ color: 'danger', message: "Couldn't upload some files. Please try again." });
      }
    };

    fileStackClient.picker({ ...defaultOptions, ...options }).open();
  };

  const deleteFile = async (fileHandle, onDelete) => {
    if (!fileHandle) return;
    startLoading();
    try {
      await onDelete(fileHandle);
      if (ia(fileHandle)) {
        setFiles([]);
      } else {
        setFiles((prev) => prev.filter((file) => file.handle !== fileHandle));
      }
      return true;
    } catch (err) {
      showAlert({ color: 'danger', message: err.message || "Couldn't delete the file" });
      return false;
    } finally {
      stopLoading();
    }
  };

  const readFile = async (fileHandle) => {
    const existingFile = files.find((file) => file.handle === fileHandle);
    if (existingFile) return existingFile;

    startLoading();
    try {
      const fileStackClient = await getFilestackClient();
      const result = await fileStackClient.download(fileHandle);

      const file = new File([result.data], result.headers['x-file-name'], {
        type: result.headers['content-type']
      });
      const fileData = {
        fileId: v4(),
        handle: fileHandle,
        name: file.name,
        size: file.size,
        uploadedSize: file.size,
        type: file.type,
        url: result.config.url,
        progress: 100,
        status: 'uploaded',
        addedAt: Date.now()
      };
      setFiles((prev) => {
        const fileMap = new Map(prev.map((file) => [file.handle, file]));
        fileMap.set(fileData.handle, fileData);
        return Array.from(fileMap.values());
      });
      return fileData;
    } catch (err) {
      showAlert({ color: 'danger', message: err.message || "Couldn't read the file" });
      return null;
    } finally {
      stopLoading();
    }
  };

  const cancelUpload = (fileId) => {
    uploadControllers.get(fileId)?.abort();
  };

  const retryUpload = async (fileId, onUpload) => {
    const originalFile = originalFiles.get(fileId);
    if (!originalFile) return;

    originalFiles.delete(fileId);
    setFiles((prev) => prev.filter((file) => file.fileId !== fileId));
    addFile(originalFile, onUpload);
  };

  return {
    files,
    setFiles,
    addFile,
    addFiles,
    deleteFile,
    readFile,
    retryUpload,
    openFilePicker,
    cancelUpload,
    loading
  };
};
